// Neue Finanz-Startseite (Phase 1 der Neugestaltung): grosse Schnellaktionen, "Heute zu erledigen", Finanzuebersicht, "Zuletzt bearbeitet".
// Reine Darstellung: Es werden ausschliesslich bestehende, lesende Endpunkte benutzt (finanzenApi.aufgaben/uebersicht/importListe, belegeApi.uebersichtFehlend/liste).
// Es gibt keine eigene Berechnung - jede Zahl stammt aus dem Server; ist eine Quelle nicht verfuegbar oder die Funktion nicht freigegeben, steht "–" bzw. ein Hinweis.
// Schnellaktionen fuer Belege erscheinen nur, wenn die Belegverwaltung serverseitig freigegeben ist (ctx.belege); sonst sind sie sichtbar gesperrt.
import { useState, useEffect, useRef, useCallback } from "react";
import THEME from "../../../../theme";
import { finanzenApi } from "../../services/finanzenApi";
import { belegeApi } from "../../../belege/belegeApi";
import { Chip, Klein, euro, datum } from "../../../belege/ui";
import { Seitenkopf, Abschnitt, useBreite, BREAKPOINT_KLEIN, geld } from "../../ui/Bausteine";
import { ZIEL_BELEGE_AUFGABEN, sicher, heuteMonat, monatsGrenzen } from "./UeberblickSeiten";
import { zuletztGeoeffnet } from "../../utils/zuletzt";

const sichereListe = (p) => sicher(p);

function Schnellaktion({ symbol, titel, hinweis, gesperrt, onClick, children }) {
  const stil = {
    textAlign: "left", background: THEME.colors.surface, color: gesperrt ? THEME.colors.textMuted : THEME.colors.text, border: `1px solid ${THEME.colors.border}`, borderRadius: THEME.radius.xl,
    padding: "16px", minHeight: 112, width: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 8, cursor: gesperrt ? "not-allowed" : "pointer",
    opacity: gesperrt ? 0.7 : 1, font: "inherit",
  };
  return (
    <button type="button" onClick={gesperrt ? undefined : onClick} aria-disabled={gesperrt ? "true" : undefined} style={stil}>
      <span aria-hidden="true" style={{ fontSize: 28, lineHeight: 1 }}>{symbol}</span>
      <span>
        <strong style={{ display: "block", fontSize: THEME.fontSize.md }}>{titel}</strong>
        {hinweis && <span style={{ display: "block", color: gesperrt ? THEME.colors.warning : THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, marginTop: 2 }}>{hinweis}</span>}
      </span>
      {children}
    </button>
  );
}

function Aufgabe({ anzahl, titel, hinweis, onClick, dringend }) {
  return (
    <button type="button" onClick={onClick} style={{ display: "flex", gap: 12, alignItems: "center", width: "100%", textAlign: "left", background: "transparent", color: THEME.colors.text, border: "none",
      borderTop: `1px solid ${THEME.colors.border}`, padding: "10px 0", minHeight: 56, cursor: "pointer", font: "inherit" }}>
      <span style={{ minWidth: 52, textAlign: "center", fontWeight: 800, fontSize: THEME.fontSize.lg, borderRadius: THEME.radius.md, padding: "8px 6px", background: THEME.colors.surfaceMuted, color: dringend ? THEME.colors.danger : THEME.colors.warning }}>{anzahl}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <strong style={{ display: "block", overflowWrap: "anywhere" }}>{titel}</strong>
        {hinweis && <span style={{ display: "block", color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, overflowWrap: "anywhere" }}>{hinweis}</span>}
      </span>
      <span aria-hidden="true" style={{ fontSize: 22, color: THEME.colors.textSecondary }}>›</span>
    </button>
  );
}

function Wert({ titel, wert, farbe, hinweis }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>{titel}</div>
      <div style={{ color: farbe || THEME.colors.text, fontSize: THEME.fontSize.xl, fontWeight: 700, overflowWrap: "anywhere" }}>{wert}</div>
      {hinweis && <div style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, overflowWrap: "anywhere" }}>{hinweis}</div>}
    </div>
  );
}

// Kleine Balkenanzeige der letzten Monate (vorhandene Werte "vorlaeufiges_betriebsergebnis" der Monatsentwicklung; nur Skalierung fuer die Darstellung)
function Verlauf({ monate }) {
  const werte = (monate || []).slice(-6);
  const max = Math.max(1, ...werte.map(m => Math.abs(m.vorlaeufiges_betriebsergebnis || 0)));
  return (
    <div role="img" aria-label="Vorläufiges Betriebsergebnis der letzten Monate (Bankumsätze)" style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 64, marginTop: 8 }}>
      {werte.map(m => {
        const w = m.vorlaeufiges_betriebsergebnis || 0;
        return (
          <div key={`${m.jahr}-${m.monat}`} title={`${String(m.monat).padStart(2, "0")}/${m.jahr}: ${geld(w, true)}`} style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end", height: "100%", alignItems: "center", minWidth: 0 }}>
            <div style={{ width: "100%", height: `${Math.max(4, Math.round(Math.abs(w) / max * 100))}%`, background: w < 0 ? THEME.colors.danger : THEME.colors.success, borderRadius: 3, opacity: 0.85 }} />
            <div style={{ fontSize: 10, color: THEME.colors.textMuted, marginTop: 2 }}>{String(m.monat).padStart(2, "0")}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function FinanzStart({ benutzerId, ctx, onNavigiere, onDateien }) {
  const breite = useBreite();
  const klein = breite < BREAKPOINT_KLEIN;
  const [monat, setMonat] = useState(heuteMonat());
  const [d, setD] = useState(null);
  const datei = useRef(null);
  const kamera = useRef(null);

  const laden = useCallback(async () => {
    const g = monatsGrenzen(monat);
    const [aufgaben, uebersicht, imports] = await Promise.all([
      sichereListe(finanzenApi.aufgaben(benutzerId)), sichereListe(finanzenApi.uebersicht(benutzerId, { von: g.von, bis: g.bis })), sichereListe(finanzenApi.importListe(benutzerId, { seiten_groesse: 3 })),
    ]);
    const fehlend = ctx.belege ? await sichereListe(belegeApi.uebersichtFehlend()) : null;
    const belege = ctx.belege ? await sichereListe(belegeApi.liste({ limit: 4 })) : null;
    setD({ g, aufgaben, uebersicht, imports, fehlend, belege });
  }, [benutzerId, ctx.belege, monat]);
  useEffect(() => { setD(null); laden(); }, [laden]);

  const dateienGewaehlt = (dateien, quelle) => { if (dateien?.length) onDateien(Array.from(dateien), quelle); };

  // "Heute zu erledigen": nur tatsaechlich vorhandene Aufgaben (Reihenfolge: Belege/Bank zuerst, dann die uebrigen Bank-Aufgaben)
  const aufgabenListe = [];
  if (d?.fehlend?.ok) {
    const b = d.fehlend.daten.bereiche;
    const reihenfolge = ["umsaetze_ohne_beleg", "ohne_bankzuordnung", ...Object.keys(b).filter(k => !["umsaetze_ohne_beleg", "ohne_bankzuordnung"].includes(k))];
    reihenfolge.forEach(k => {
      const e = b[k];
      if (e && e.anzahl > 0 && ZIEL_BELEGE_AUFGABEN[k]) {
        const z = ZIEL_BELEGE_AUFGABEN[k];
        aufgabenListe.push({ key: `b-${k}`, anzahl: e.anzahl, titel: e.titel, hinweis: e.hinweis, ziel: () => onNavigiere(z[0], z[1], z[2]), dringend: k === "umsaetze_ohne_beleg" });
      }
    });
  }
  if (d?.aufgaben?.ok) {
    d.aufgaben.daten.aufgaben.forEach(a => aufgabenListe.push({ key: `a-${a.typ}`, anzahl: a.anzahl, titel: a.titel, ziel: () => onNavigiere(null, a.ziel_tab, a.ziel_filter), dringend: a.typ === "pruefung_erforderlich" }));
  }

  const per = d?.uebersicht?.ok ? d.uebersicht.daten.aktuelle_periode : null;
  const vor = d?.uebersicht?.ok ? d.uebersicht.daten.vormonat : null;
  const verlauf = d?.uebersicht?.ok ? d.uebersicht.daten.monatsentwicklung : null;
  const letzteImporte = d?.imports?.ok ? (d.imports.daten.eintraege || []).slice(0, 3) : [];
  const zuletzt = zuletztGeoeffnet.lesen().filter(v => ctx.belege || v.typ !== "beleg").slice(0, 4);
  const gesperrt = !ctx.belege;
  const sperrHinweis = "Belegverwaltung nicht freigegeben";

  const raster = { display: "grid", gridTemplateColumns: klein ? "1fr 1fr" : "repeat(4, minmax(0, 1fr))", gap: klein ? 10 : 14, marginBottom: 16 };
  const zweispaltig = { display: "grid", gridTemplateColumns: breite >= 1024 ? "minmax(0, 3fr) minmax(0, 2fr)" : "1fr", gap: 16, alignItems: "start" };

  return (
    <div style={{ color: THEME.colors.text }}>
      <Seitenkopf titel="Startseite" untertitel="Das Wichtigste zuerst: Belege erfassen, Bank importieren und was heute zu tun ist."
        rechts={<label style={{ display: "flex", gap: 8, alignItems: "center", color: THEME.colors.textSecondary }}>Monat
          <input type="month" aria-label="Monat" style={{ padding: "10px 14px", borderRadius: THEME.radius.md, border: `1px solid ${THEME.colors.border}`, background: THEME.colors.background, color: THEME.colors.text, fontSize: THEME.fontSize.base }}
            value={monat} onChange={e => e.target.value && setMonat(e.target.value)} /></label>} />

      <input ref={datei} type="file" multiple accept="image/*,.heic,.heif,application/pdf" style={{ display: "none" }} aria-label="Beleg-Datei wählen" onChange={e => { dateienGewaehlt(e.target.files, "upload"); e.target.value = ""; }} />
      <input ref={kamera} type="file" accept="image/*" capture="environment" style={{ display: "none" }} aria-label="Beleg fotografieren" onChange={e => { dateienGewaehlt(e.target.files, "kamera"); e.target.value = ""; }} />
      <div style={raster} role="group" aria-label="Schnellaktionen">
        <Schnellaktion symbol="📷" titel="Beleg fotografieren" gesperrt={gesperrt} hinweis={gesperrt ? sperrHinweis : "Kamera öffnen"} onClick={() => kamera.current?.click()} />
        <Schnellaktion symbol="⬆️" titel="Beleg hochladen" gesperrt={gesperrt} hinweis={gesperrt ? sperrHinweis : "Foto oder PDF wählen"} onClick={() => datei.current?.click()} />
        <Schnellaktion symbol="🏦" titel="Bankumsätze importieren" hinweis="CSV-Import" onClick={() => onNavigiere("bank", "import")} />
        <Schnellaktion symbol="🔎" titel="Belege suchen" gesperrt={gesperrt} hinweis={gesperrt ? sperrHinweis : "Alle Belege durchsuchen"} onClick={() => onNavigiere("belege", "liste", { fokus: "suche" })} />
      </div>

      <div style={zweispaltig}>
        <div style={{ minWidth: 0 }}>
          <Abschnitt titel="Heute zu erledigen">
            {!d && <div style={{ color: THEME.colors.textMuted }}>Lädt…</div>}
            {d && aufgabenListe.length === 0 && <div style={{ color: THEME.colors.success }}>✓ Aktuell ist nichts offen.</div>}
            {d && aufgabenListe.map(a => <Aufgabe key={a.key} anzahl={a.anzahl} titel={a.titel} hinweis={a.hinweis} onClick={a.ziel} dringend={a.dringend} />)}
            {d && !d.aufgaben.ok && <Klein farbe={THEME.colors.warning}>Bank-Aufgaben nicht verfügbar ({d.aufgaben.grund}).</Klein>}
            {d && ctx.belege && d.fehlend && !d.fehlend.ok && <Klein farbe={THEME.colors.warning}>Beleg-Aufgaben nicht verfügbar ({d.fehlend.grund}).</Klein>}
            {d && !ctx.belege && <Klein>Aufgaben zu Belegen erscheinen, sobald die Belegverwaltung freigegeben ist.</Klein>}
          </Abschnitt>
        </div>

        <div style={{ minWidth: 0 }}>
          <Abschnitt titel={`Finanzübersicht ${String(d?.g?.monat || monatsGrenzen(monat).monat).padStart(2, "0")}/${d?.g?.jahr || monatsGrenzen(monat).jahr}`}>
            {!d && <div style={{ color: THEME.colors.textMuted }}>Lädt…</div>}
            {d && !d.uebersicht.ok && <Klein farbe={THEME.colors.warning}>Nicht verfügbar ({d.uebersicht.grund}).</Klein>}
            {d && per && (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  <Wert titel="Einnahmen" wert={geld(per.einnahmen_betrieblich)} farbe={THEME.colors.success} hinweis={vor ? `Vormonat ${geld(vor.einnahmen_betrieblich)}` : undefined} />
                  <Wert titel="Ausgaben" wert={geld(Math.abs(per.ausgaben_betrieblich))} farbe={THEME.colors.danger} hinweis={vor ? `Vormonat ${geld(Math.abs(vor.ausgaben_betrieblich))}` : undefined} />
                </div>
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${THEME.colors.border}` }}>
                  <Wert titel="Monatsstand (vorläufiges Ergebnis)" wert={geld(per.vorlaeufiges_betriebsergebnis, true)} farbe={per.vorlaeufiges_betriebsergebnis < 0 ? THEME.colors.danger : THEME.colors.success}
                    hinweis={vor ? `Vormonat ${geld(vor.vorlaeufiges_betriebsergebnis, true)}` : undefined} />
                  {verlauf && verlauf.length > 0 && <Verlauf monate={verlauf} />}
                </div>
                <Klein>Grundlage: importierte Bankumsätze (betriebsrelevant), nach Zahlungsdatum. Keine Buchhaltung – maßgeblich bleibt die Auswertung des Steuerberaters.</Klein>
                <div style={{ marginTop: 10 }}>
                  <button type="button" onClick={() => onNavigiere("uebersicht", "einnahmen-ausgaben")} style={{ background: "transparent", color: THEME.colors.primary, border: "none", padding: "12px 0", cursor: "pointer", font: "inherit", minHeight: 48 }}>Einnahmen und Ausgaben im Detail ›</button>
                </div>
              </>
            )}
          </Abschnitt>
        </div>
      </div>

      <Abschnitt titel="Zuletzt bearbeitet">
        {!d && <div style={{ color: THEME.colors.textMuted }}>Lädt…</div>}
        {d && (
          <div style={{ display: "grid", gridTemplateColumns: breite >= 1024 ? "repeat(3, minmax(0, 1fr))" : "1fr", gap: 16 }}>
            <div style={{ minWidth: 0 }}>
              <strong>Letzte Belege</strong>
              {!ctx.belege && <Klein>{sperrHinweis}</Klein>}
              {ctx.belege && d.belege && !d.belege.ok && <Klein farbe={THEME.colors.warning}>Nicht verfügbar ({d.belege.grund}).</Klein>}
              {ctx.belege && d.belege?.ok && d.belege.daten.belege.length === 0 && <Klein>Noch keine Belege.</Klein>}
              {ctx.belege && d.belege?.ok && d.belege.daten.belege.map(b => (
                <button type="button" key={b.id} onClick={() => onNavigiere("belege", "liste", { beleg: b.id })} style={zeile}>
                  <span style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><strong>{b.beleg_nr}</strong><Chip status={b.status} /></span>
                  <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, overflowWrap: "anywhere" }}>{b.lieferant || "Lieferant fehlt"} · {euro(b.brutto)} · {datum(b.erfasst_am)}</span>
                </button>
              ))}
            </div>
            <div style={{ minWidth: 0 }}>
              <strong>Letzte Bankimporte</strong>
              {d.imports && !d.imports.ok && <Klein farbe={THEME.colors.warning}>Nicht verfügbar ({d.imports.grund}).</Klein>}
              {d.imports?.ok && letzteImporte.length === 0 && <Klein>Noch kein Import.</Klein>}
              {letzteImporte.map(i => (
                <button type="button" key={i.id} onClick={() => onNavigiere("bank", "import")} style={zeile}>
                  <strong style={{ overflowWrap: "anywhere" }}>{i.dateiname}</strong>
                  <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>{(i.importiert_am || "").slice(0, 10)} · {i.zeilen_importiert} Zeilen{i.zeilen_fehler ? `, ${i.zeilen_fehler} Fehler` : ""}</span>
                </button>
              ))}
            </div>
            <div style={{ minWidth: 0 }}>
              <strong>Zuletzt geöffnete Vorgänge</strong>
              {zuletzt.length === 0 && <Klein>Noch keine – hier erscheinen Belege und Bankumsätze, die du auf diesem Gerät zuletzt geöffnet hast.</Klein>}
              {zuletzt.map(v => (
                <button type="button" key={`${v.typ}-${v.id}`} onClick={() => (v.typ === "beleg" ? onNavigiere("belege", "liste", { beleg: v.id }) : onNavigiere("bank", "bankumsaetze", { suche: v.suche || "" }))} style={zeile}>
                  <strong style={{ overflowWrap: "anywhere" }}>{v.typ === "beleg" ? "Beleg" : "Bankumsatz"} {v.titel}</strong>
                  <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, overflowWrap: "anywhere" }}>{v.untertitel}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </Abschnitt>
    </div>
  );
}

const zeile = { display: "flex", flexDirection: "column", gap: 2, width: "100%", textAlign: "left", background: "transparent", color: THEME.colors.text, border: "none", borderTop: `1px solid ${THEME.colors.border}`,
  padding: "10px 0", minHeight: 56, cursor: "pointer", font: "inherit", marginTop: 0 };
